'use client';

import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

const SPEED = 56;

type Motion = { period: number; copies: number; duration: number; delay: number };

export default function DriftRow({
  children,
  direction,
  offset,
  className,
}: {
  children: ReactNode;
  direction: 'left' | 'right';
  offset: { mobile: number; desktop: number };
  className?: string;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const [motion, setMotion] = useState<Motion | null>(null);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    const group = groupRef.current;
    if (!strip || !group) return;
    const measure = () => {
      const period = group.offsetWidth;
      if (!period) return;
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 8;
      const speed = (SPEED * rem) / 8;
      const start = strip.offsetWidth / 2 + (window.innerWidth >= 993 ? offset.desktop : offset.mobile) * rem;
      const phase = (((-start % period) + period) % period);
      const lead = direction === 'left' ? phase : (period - phase) % period;
      setMotion({
        period,
        copies: Math.max(2, Math.ceil(strip.offsetWidth / period) + 1),
        duration: period / speed,
        delay: -lead / speed,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    observer.observe(group);
    return () => observer.disconnect();
  }, [direction, offset.desktop, offset.mobile]);

  const style = motion
    ? ({
        '--drift-p': `${motion.period}px`,
        animationDuration: `${motion.duration}s`,
        animationDelay: `${motion.delay}s`,
      } as CSSProperties)
    : ({ animationPlayState: 'paused' } as CSSProperties);

  return (
    <div
      ref={stripRef}
      className={cn('overflow-hidden', className)}>
      <div
        className={cn('riz-drift flex w-max', direction === 'right' && 'riz-drift-right')}
        style={style}>
        {Array.from({ length: motion?.copies ?? 2 }, (_, copy) => (
          <div
            key={copy}
            ref={copy === 0 ? groupRef : undefined}
            aria-hidden={copy > 0 || undefined}
            className="flex shrink-0">
            {children}
          </div>
        ))}
      </div>
      <style jsx>{`
        .riz-drift {
          animation: riz-drift-left 1s linear infinite;
          will-change: transform;
        }
        .riz-drift-right {
          animation-name: riz-drift-right;
        }
        @keyframes riz-drift-left {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(calc(-1 * var(--drift-p)));
          }
        }
        @keyframes riz-drift-right {
          from {
            transform: translateX(calc(-1 * var(--drift-p)));
          }
          to {
            transform: translateX(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .riz-drift {
            animation-play-state: paused;
          }
        }
      `}</style>
    </div>
  );
}
